import React from 'react'
import { Redirect, RouteComponentProps } from 'react-router-dom'

import {
  GetDetailViewArtboardQuery,
  useGetDetailViewArtboardQuery,
} from '@sketch/gql-types'
import {
  RouteParams,
  routes,
  useQueryRetry,
  versionedRoutes,
} from '@sketch/modules-common'

import { useVersioning } from '../../../../versioning'
import { LoadingState } from '@sketch/components'

const getArtboard = (result: { data?: GetDetailViewArtboardQuery }) => {
  const artboard = result?.data?.artboard
  // at some point Apollo (while loading) returns an artboard which contains only
  // a symbol of id, all other properties are non existing
  // this gives a false impression, that the object is there, but it isn't
  return artboard?.identifier ? artboard : undefined
}

interface UseRetryGetDetailViewArtboardQueryProps {
  permanentArtboardShortId: string
  versionShortId?: string
  shareIdentifier: string
  enabled: boolean
}

const useRetryGetDetailViewArtboardQuery = ({
  permanentArtboardShortId,
  versionShortId,
  shareIdentifier,
  enabled = true,
}: UseRetryGetDetailViewArtboardQueryProps) =>
  useQueryRetry(
    useGetDetailViewArtboardQuery({
      shouldInvalidatePrevious: (prev, curr) =>
        prev?.permanentArtboardShortId !== curr?.permanentArtboardShortId,
      // To account for artboard updates we're setting a cache-and-network
      // policy for artboard queries without an explicit version.
      fetchPolicy: versionShortId ? 'cache-first' : 'cache-and-network',
      variables: {
        permanentArtboardShortId: permanentArtboardShortId,
        documentVersionShortId: versionShortId,
        shareIdentifier,
      },
      skip: !enabled,
    }),
    {
      retryIf: result => {
        if (!enabled) return false
        const artboard = getArtboard(result)
        return !artboard
      },
    }
  )

type ArtboardDetailViewRouteProps = RouteComponentProps<
  RouteParams<'ARTBOARD_DETAIL'>
>

export interface ArtboardDetailQueriesProps
  extends ArtboardDetailViewRouteProps {}

/**
 * This code allows old URLS that are on the artboard view to be redirected to
 * the newer frame view.
 */
export const ArtboardDetailQueries = ({
  match,
  location,
}: ArtboardDetailQueriesProps) => {
  const { permanentArtboardShortId } = match.params

  const versionContext = useVersioning()
  const { versionShortId, share } = versionContext

  const artboardAtDocResult = useRetryGetDetailViewArtboardQuery({
    permanentArtboardShortId,
    versionShortId,
    shareIdentifier: share.identifier,
    enabled: true,
  })

  const artboardAtLatestResult = useRetryGetDetailViewArtboardQuery({
    permanentArtboardShortId,
    /*
     * passing latestVersionId does not work
     * as on the latest version of the document the artboard might be deleted
     * leaving this comment here, so that if anyone would
     * try to optimise it in the future, they would be aware
     * more: https://github.com/sketch-hq/cloud-frontend/pull/800#discussion_r289356950
     */
    // versionId={latestVersionId}
    shareIdentifier: share.identifier,
    enabled: true,
  })

  const error = artboardAtDocResult.error || artboardAtLatestResult.error
  const loading = artboardAtDocResult.loading || artboardAtLatestResult.loading

  const artboards = {
    docVersion: getArtboard(artboardAtDocResult),
    latest: getArtboard(artboardAtLatestResult),
  }

  if (loading) {
    return <LoadingState />
  }

  if (error || !artboards.latest || !artboards.docVersion) {
    return (
      <Redirect
        to={routes.SHARE_VIEW.create({
          shareID: share.identifier,
        })}
        push={false}
      />
    )
  }

  const isLatestVersion =
    artboards.latest.documentVersionShortId ===
    artboards.docVersion.documentVersionShortId

  const redirectURL = isLatestVersion
    ? versionedRoutes.FRAME.LATEST.create({
        shareID: share.identifier,
        frameUUID: artboards.latest.uuid,
      })
    : versionedRoutes.FRAME.VERSION.create({
        shareID: share.identifier,
        frameUUID: artboards.latest.uuid,
        versionShortId: artboards.docVersion.documentVersionShortId,
      })

  return <Redirect to={{ ...location, pathname: redirectURL }} />
}
